import { lazy, Suspense } from "react";
import { useStudioLocale } from "@/i18n";
import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

/**
 * Bundle boundary for the Report workspace surface.
 *
 * WHY. `features/report-workspace/report-workspace-surface.tsx` (82,422 source
 * bytes) was in the initial Studio chunk because `IDEShellLayout` imported it
 * statically through a one-line re-export facade. Nothing renders it at first
 * paint: `ActiveSurface` is a switch over `activeRailItem` and mounts exactly
 * one surface, `INITIAL_ACTIVE_RAIL_ITEM` is `'preview'` (rail-defaults.ts),
 * and no other module imports the component — the facade was its only
 * importer, and this file replaces it. The bytes were paid by every session
 * and read by the ones that open Report.
 *
 * WHY THE SURFACE AND NOT ITS COPY TABLE. `report-workspace-copy.ts` (59,687
 * source bytes, 5 locales) does NOT move with this boundary and is not meant
 * to: `report-workspace-model.ts` imports it, and the model is reached from
 * `services/project-api.ts`, `mcp/register-studio-tools.ts`,
 * `agent-workspace/report-workspace-bridge.ts` and `evidence-surface.tsx`, all
 * of which stay eager. Deferring the copy would therefore recover nothing
 * while adding the build-default-locale flash that the `largestJavaScriptBytes`
 * note in scripts/studio-bundle-budget.mjs records as the reason co-located
 * copy tables are generally not made async. What is deferred here is only the
 * SURFACE, and nothing of it is on screen before the chunk lands.
 *
 * Same shape, same reasoning, as `EvidenceSurfaceLazy` in
 * `evidence-surface-lazy.tsx` and `AsIsView` in `IDEShellLayout.tsx`.
 *
 * The lazy type is created ONCE at module scope. Recreating it inside a
 * component makes React treat every render as a new lazy component and repaint
 * the Suspense fallback — the trap `App.tsx` documents for its route elements.
 *
 * The dynamic import names the feature module directly rather than the former
 * `surfaces/report-workspace-surface.tsx` facade: importing a re-export facade
 * makes rolldown emit a second, ~130-byte chunk whose NAME still matches the
 * surface, which is the false-green the `evidence-surface-copy-eager` note
 * records having hit when that gate was first written against a name prefix.
 */
const ReportWorkspaceSurfaceChunk = lazy(() =>
  import("@/features/report-workspace/report-workspace-surface").then((m) => ({
    default: m.ReportWorkspaceSurface,
  })),
);

/**
 * The wait state for the Report chunk.
 *
 * Deliberately the generic "loading" register rather than any of the surface's
 * own framing, and deliberately NOT read from `report-workspace-copy.ts`: that
 * table stays eager for the reasons above, so reading it here would work but
 * would tie this placeholder to a module this boundary makes no claim about.
 */
const LOADING_COPY: Record<LocaleCode, string> = {
  en: "Loading report workspace…",
  ko: "보고서 작업 공간을 불러오는 중…",
  ja: "レポートワークスペースを読み込んでいます…",
  zh: "正在加载报告工作区…",
  "zh-TW": "正在載入報告工作區…",
};

/** Exported for the test that pins the wait state in every locale. */
export function ReportWorkspaceSurfaceLoadingPlaceholder() {
  const { locale } = useStudioLocale();
  return (
    <div
      data-testid="report-workspace-surface-loading-placeholder"
      className="flex h-full items-center justify-center text-sm text-muted-foreground"
    >
      {LOADING_COPY[locale] ?? LOADING_COPY.en}
    </div>
  );
}

/**
 * Drop-in replacement for a static `<ReportWorkspaceSurface />`.
 *
 * The Suspense boundary lives here rather than at the call site so that a
 * second call site cannot reintroduce a static import by accident.
 */
export function ReportWorkspaceSurfaceLazy() {
  return (
    <Suspense fallback={<ReportWorkspaceSurfaceLoadingPlaceholder />}>
      <ReportWorkspaceSurfaceChunk />
    </Suspense>
  );
}
